import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';

import { SmallLoadingButton } from '~components/Order/ordersDispatchStyledComponents';
import useMessagingMessageReadersInfiniteQuery from '~queries/useMessagingMessageReadersInfiniteQuery';

// About one line of body2 text, so the popover does not resize when the names arrive.
const LOADING_SPINNER_SIZE_IN_PX = 20;

// Fits a typical full name on one line, so the popover keeps its width as names arrive.
const READERS_LIST_MIN_WIDTH_IN_PX = 180;

/** Roughly eight rows, so a long list scrolls inside the popover like the members list. */
const READERS_LIST_MAX_HEIGHT_IN_PX = 360;

type MessageReadersListProps = {
  channelId: string;
  messageId: string;
};

export default function MessageReadersList({
  channelId,
  messageId,
}: MessageReadersListProps) {
  const theme = useTheme();
  const { fetchMore, hasMore, isError, isLoading, isLoadingMore, readers } =
    useMessagingMessageReadersInfiniteQuery({ channelId, messageId });

  const renderBody = () => {
    if (isLoading)
      return (
        <CircularProgress
          size={LOADING_SPINNER_SIZE_IN_PX}
          sx={{ alignSelf: 'center' }}
        />
      );
    if (isError && readers.length === 0) {
      return (
        <Typography
          sx={{ color: theme.semantic.textSecondary, px: 2, py: 0.75 }}
          variant="body2"
        >
          {t('error_messages.messaging.failed_to_fetch_readers')}
        </Typography>
      );
    }

    return (
      <>
        <Box
          component="ul"
          sx={{
            listStyle: 'none',
            m: 0,
            maxHeight: READERS_LIST_MAX_HEIGHT_IN_PX,
            overflowY: 'auto',
            px: 0,
            py: 0.5,
          }}
        >
          {readers.map((reader) => (
            <Box
              component="li"
              data-test-id="messaging-message-reader"
              key={reader.id}
              sx={{ px: 2, py: 0.75 }}
            >
              <Typography noWrap variant="body2">
                {reader.displayName || t('messaging.members.unknown_member')}
              </Typography>
            </Box>
          ))}
        </Box>

        {isError && (
          <Typography
            sx={{ color: theme.semantic.textSecondary, px: 2, py: 0.5 }}
            variant="caption"
          >
            {t('error_messages.messaging.failed_to_fetch_readers')}
          </Typography>
        )}

        {hasMore && (
          <SmallLoadingButton
            color="secondary"
            loading={isLoadingMore}
            onClick={() => void fetchMore()}
            variant="text"
          >
            {t('messaging.show_more_readers')}
          </SmallLoadingButton>
        )}
      </>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: READERS_LIST_MIN_WIDTH_IN_PX,
      }}
    >
      <Typography
        sx={{
          borderBottom: `1px solid ${theme.brandV2.colors.treadGray7}`,
          px: 2,
          py: 1,
        }}
        variant="subtitle2"
      >
        {t('messaging.read_by_heading')}
      </Typography>
      {renderBody()}
    </Box>
  );
}
